import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router";
import { useSelector } from "react-redux";
import {
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Sparkles,
  Home,
  Scale,
  Apple,
  HeartPulse,
  Briefcase,
  Trophy,
  Info,
  CircleHelp,
  LogIn,
  UserPlus,
  Mail,
  Users,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCategories } from "../../contexts/CategoriesContext";
import {
  INDUSTRIES,
  INDUSTRY_MENU_CATEGORIES,
} from "../../data/categoryData";
import {
  AccountNavDropdown,
  AccountNavMobileSection,
} from "./AccountNavDropdown";
import Images from "../../utils/Images";

const INDUSTRY_ICONS = {
  "real-estate": Home,
  legal: Scale,
  nutrition: Apple,
  health: HeartPulse,
  careers: Briefcase,
  "betting-gaming": Trophy,
};

/** Space between navbar bottom and mega menu panel (px). */
const MEGA_MENU_BELOW_NAV_GAP = 8;
const FALLBACK_INDUSTRIES = INDUSTRIES;
const FALLBACK_MENU_BY_INDUSTRY = INDUSTRY_MENU_CATEGORIES;

const PROVIDER_LOGIN_URL = "https://provider.infoboxx.com/login";

const GuestNavbar = ({ hideFeedback = true }) => {
  const { isAuthenticated } = useSelector((state) => state.infoboxx_consumer);
  const { industries: nextIndustries, menuByIndustry: nextMenuByIndustry } = useCategories();
  const [industries, setIndustries] = useState(FALLBACK_INDUSTRIES);
  const [menuByIndustry, setMenuByIndustry] = useState(FALLBACK_MENU_BY_INDUSTRY);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [activeIndustryId, setActiveIndustryId] = useState(
    FALLBACK_INDUSTRIES[0]?.id ?? "real-estate",
  );
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [mobileIndustryId, setMobileIndustryId] = useState(
    FALLBACK_INDUSTRIES[0]?.id ?? "real-estate",
  );
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  /** "categories" = category list visible; "subtopics" = drill into selected industry */
  const [mobileView, setMobileView] = useState("categories");
  const mobileSubtopicsRef = useRef(null);
  const location = useLocation();
  /** On /partners* routes, auth CTAs target the provider portal instead of consumer auth. */
  const isPartnersRoute = location.pathname.startsWith("/partners");
  const signInHref = isPartnersRoute ? PROVIDER_LOGIN_URL : "/signin";
  const registerHref = isPartnersRoute ? "/partners/onboard" : "/signup";
  const registerLabel = isPartnersRoute ? "Register" : "Register Free";
  const navRef = useRef(null);
  const megaMenuRef = useRef(null);
  const exploreBtnRef = useRef(null);
  /** After scroll: full-width bar docked to top. At top: floating pill (default). */
  const [isDockedBar, setIsDockedBar] = useState(() =>
    typeof window !== "undefined" ? window.scrollY > 20 : false,
  );
  /** Viewport Y (px) where fixed mega menu panel starts — portaled to body so it is not clipped. */
  const [megaMenuTop, setMegaMenuTop] = useState(0);
  /** Viewport X (px) where fixed mega menu panel starts. */
  const [megaMenuLeft, setMegaMenuLeft] = useState(0);
  const [megaMenuWidth, setMegaMenuWidth] = useState(0);

  const activeIndustry =
    industries.find((i) => i.id === activeIndustryId) ?? industries[0];
  const ActiveIndustryHubIcon =
    INDUSTRY_ICONS[activeIndustryId] || Home;
  const activeCategories = useMemo(
    () => menuByIndustry[activeIndustryId] ?? [],
    [menuByIndustry, activeIndustryId],
  );

  const mobileIndustry =
    industries.find((i) => i.id === mobileIndustryId) ?? industries[0];
  const mobileCategories = menuByIndustry[mobileIndustryId] ?? [];

  useEffect(() => {
    if (!nextIndustries.length) return;

    setIndustries(nextIndustries);
    setMenuByIndustry(nextMenuByIndustry);
    setActiveIndustryId((prev) =>
      nextIndustries.some((i) => i.id === prev) ? prev : nextIndustries[0].id,
    );
    setMobileIndustryId((prev) =>
      nextIndustries.some((i) => i.id === prev) ? prev : nextIndustries[0].id,
    );
  }, [nextIndustries, nextMenuByIndustry]);

  useEffect(() => {
    if (activeCategories[0]) setActiveCategoryId(activeCategories[0].id);
  }, [activeCategories]);

  const updateMegaTop = useCallback(() => {
    if (!navRef.current) return;
    setMegaMenuTop(Math.round(navRef.current.getBoundingClientRect().bottom));

    const viewportWidth = window.innerWidth;
    const targetWidth = Math.min(viewportWidth - 32, 1180); // wide enough for 10 compact topics
    const edgeGap = 24;
    const exploreRect = exploreBtnRef.current?.getBoundingClientRect();
    const preferredCenter = exploreRect
      ? exploreRect.left + exploreRect.width / 2
      : viewportWidth / 2;
    const preferredLeft = preferredCenter - targetWidth / 2;
    const maxLeft = viewportWidth - targetWidth - edgeGap;
    const clampedLeft = Math.max(edgeGap, Math.min(preferredLeft, maxLeft));
    setMegaMenuLeft(Math.round(clampedLeft));
    setMegaMenuWidth(Math.round(targetWidth));
  }, []);

  useLayoutEffect(() => {
    updateMegaTop();
    const ro = new ResizeObserver(updateMegaTop);
    if (navRef.current) ro.observe(navRef.current);
    window.addEventListener("scroll", updateMegaTop, { passive: true });
    window.addEventListener("resize", updateMegaTop);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", updateMegaTop);
      window.removeEventListener("resize", updateMegaTop);
    };
  }, [updateMegaTop, isDockedBar]);

  // Close mega menu on outside click (mega is portaled — check both refs)
  useEffect(() => {
    const onDown = (e) => {
      const n = navRef.current?.contains(e.target);
      const m = megaMenuRef.current?.contains(e.target);
      if (!n && !m) setIsCategoriesOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") {
        setIsCategoriesOpen(false);
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  useEffect(() => {
    setIsCategoriesOpen(false);
    setIsMenuOpen(false);
    setMobileView("categories");
    setMobileCategoriesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const onScroll = () => {
      setIsDockedBar(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const isPillNav = !isDockedBar;

  return (
    <nav
      ref={navRef}
      className="fixed top-0 right-0 left-0 z-[100] overflow-visible"
    >
      {/*
       * Default (hero top): inset pill — white glass on dark hero.
       * Scrolled: full-width docked bar — same whitish treatment, edge-to-edge.
       */}
      <div
        className={`transition-all duration-300 ${
          isPillNav ? "px-3 sm:px-6 lg:px-10" : ""}`}
      >
        <div
          className={`flex items-center justify-between h-16 sm:h-[70px] transition-all duration-300 ${
            isPillNav
              ? "mt-2 rounded-full border border-white/40 bg-white/85 backdrop-blur-xl shadow-[0_12px_40px_-8px_rgba(0,0,0,0.25)] px-4 sm:px-6"
              : "rounded-none border-b border-black/10 bg-white/90 backdrop-blur-xl px-4 sm:px-6 lg:px-8"
          }`}
        >
          {/* ── Logo ──────────────────────────────────────────── */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <img
              src={Images.infoboxx_logo}
              alt="Infoboxx"
              className="object-contain w-auto h-8"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </Link>

          {/* ── Desktop nav links ──────────────────────────────── */}
          <div className="hidden gap-1 items-center lg:flex">
            <Link
              to="/"
              className={`rounded-lg px-3 py-2 text-base font-semibold transition-colors ${
                isActive("/")
                  ? "text-[#FFD221]"
                  : "text-black/70 hover:bg-black/10 hover:text-black"
              }`}
            >
              Home
            </Link>

            {/* Explore trigger — just a button, mega menu is hoisted out */}
            <button
              ref={exploreBtnRef}
              type="button"
              onClick={() => setIsCategoriesOpen((v) => !v)}
              className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-base font-semibold transition-colors ${
                isCategoriesOpen
                  ? "bg-[#FFD221] text-black"
                  : "text-black/80 hover:bg-black/10 hover:text-black"
              }`}
            >
              Explore
              <ChevronDown
                size={15}
                className={`transition-transform duration-200 ${isCategoriesOpen ? "rotate-180" : ""}`}
              />
            </button>

            {/* Static nav links */}
            {[
              { to: "/about", label: "About" },
              { to: "/partners", label: "Partners" },
              { to: "/faqs", label: "FAQs" },
              { to: "/contact", label: "Contact" },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`rounded-lg px-3 py-2 text-base font-semibold transition-colors ${
                  isActive(to)
                    ? "text-[#FFD221]"
                    : "text-black/70 hover:bg-black/10 hover:text-black"
                }`}
              >
                {label}
              </Link>
            ))}
          </div>

          {/* ── Desktop auth ───────────────────────────────────── */}
          <div className="hidden gap-2 items-center lg:flex">
            {isAuthenticated && !isPartnersRoute ? (
              <AccountNavDropdown />
            ) : (
              <>
                {isPartnersRoute ? (
                  <a
                    href={signInHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 text-base font-semibold rounded-full transition-colors text-black/75 hover:bg-black/10 hover:text-black"
                  >
                    Sign In
                  </a>
                ) : (
                  <Link
                    to={signInHref}
                    className="px-4 py-2 text-base font-semibold rounded-full transition-colors text-black/75 hover:bg-black/10 hover:text-black"
                  >
                    Sign In
                  </Link>
                )}
                <Link
                  to={registerHref}
                  className="px-4 py-2 text-base font-black text-black rounded-full transition hover:opacity-90 hover:scale-105"
                  style={{ background: "#FFD221" }}
                >
                  {registerLabel}
                </Link>
              </>
            )}
          </div>

          {/* ── Mobile hamburger (nav is always light surface) ───── */}
          <button
            type="button"
            className="flex justify-center items-center min-h-[44px] min-w-[44px] text-black rounded-xl border transition-colors active:scale-[0.97] lg:hidden border-black/15 hover:bg-black/10"
            onClick={() => setIsMenuOpen((v) => !v)}
            aria-expanded={isMenuOpen}
            aria-controls="guest-mobile-drawer"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mega menu: portaled, centered panel (not full-bleed) */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {isCategoriesOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  style={{ top: megaMenuTop + MEGA_MENU_BELOW_NAV_GAP }}
                  className="hidden lg:fixed lg:left-0 lg:right-0 lg:bottom-0 lg:z-[109] lg:block bg-black/10 backdrop-blur-[1px]"
                  aria-hidden
                  onClick={() => setIsCategoriesOpen(false)}
                />
                <motion.div
                  ref={megaMenuRef}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  style={{
                    top: megaMenuTop + MEGA_MENU_BELOW_NAV_GAP,
                    left: megaMenuLeft || 24,
                    width: megaMenuWidth || undefined,
                    maxHeight: `calc(100dvh - ${megaMenuTop + MEGA_MENU_BELOW_NAV_GAP}px - max(12px, env(safe-area-inset-bottom, 0px)))`,
                  }}
                  className="fixed z-[110] hidden flex-col overflow-hidden rounded-[1.25rem] border border-black/10 bg-white shadow-[0_28px_80px_-35px_rgba(0,0,0,0.65)] lg:flex"
                >
            <div className="flex min-h-0 flex-1 overflow-hidden">
              {/* ── Industry sidebar (click to select, no hover swap) ── */}
              <div className="flex w-[17rem] shrink-0 flex-col gap-px bg-[#f7f4ec] p-2">
                <p className="mb-0.5 px-2 py-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                  Categories
                </p>
                {industries.map((ind) => {
                  const IndIcon = INDUSTRY_ICONS[ind.id] || Home;
                  const isAct = ind.id === activeIndustryId;
                  return (
                    <button
                      key={ind.id}
                      type="button"
                      title={ind.label}
                      onClick={() => setActiveIndustryId(ind.id)}
                      className={`flex w-full min-w-0 items-center gap-2 rounded-md px-2.5 py-2 text-left text-[14px] font-semibold leading-snug transition-all ${
                        isAct
                          ? "bg-[#FFD221] text-black"
                          : "text-black/70 hover:bg-white hover:text-black"
                      }`}
                    >
                      <IndIcon size={17} className="shrink-0" aria-hidden />
                      <span className="whitespace-nowrap">{ind.label}</span>
                      {isAct && (
                        <ChevronRight size={15} className="ml-auto shrink-0" aria-hidden />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* ── Subcategories (right column) — compact fit within viewport ── */}
              <div className="relative flex min-h-0 min-w-0 flex-1 flex-col border-l border-black/[0.06] bg-gradient-to-br from-white via-white to-[#f7f4ec]/40">
                <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain p-3 [@media(max-height:780px)]:p-2 [@media(max-height:880px)]:overflow-y-hidden">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={activeIndustryId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                      className="flex min-h-0 min-w-0 flex-1 flex-col"
                    >
                  {/* Industry hub — primary action above sub-topic grid */}
                  <div className="mb-2.5 shrink-0 space-y-2 [@media(max-height:780px)]:mb-2 [@media(max-height:720px)]:mb-1.5">
                    <Link
                      to={activeIndustry?.path || `/${activeIndustryId}`}
                      onClick={() => setIsCategoriesOpen(false)}
                      style={{
                        borderLeftColor: activeIndustry?.color ?? "#FFD221",
                      }}
                      className="group flex w-full min-w-0 items-center gap-3 rounded-xl border border-black/[0.08] border-l-[3px] bg-white/95 px-3 py-2.5 shadow-sm transition-all hover:border-black/12 hover:bg-white hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFD221] [@media(max-height:780px)]:gap-2.5 [@media(max-height:780px)]:px-2.5 [@media(max-height:780px)]:py-2 [@media(max-height:720px)]:py-1.5"
                    >
                      <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 transition-colors group-hover:bg-[#f7f4ec] [@media(max-height:720px)]:h-9 [@media(max-height:720px)]:w-9"
                        style={{
                          color: activeIndustry?.color ?? "#6b7280",
                        }}
                      >
                        <ActiveIndustryHubIcon
                          size={19}
                          className="shrink-0"
                          strokeWidth={2.25}
                          aria-hidden
                        />
                      </span>
                      <div className="min-w-0 flex-1 text-left">
                        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
                          Industry hub
                        </p>
                        <p className="mt-0.5 text-sm font-black leading-tight text-gray-950 sm:text-base [@media(max-height:720px)]:line-clamp-1 [@media(max-height:720px)]:text-[13px]">
                          {activeIndustry?.label}
                        </p>
                      </div>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#FFD221] text-black transition group-hover:bg-[#ffdf44] [@media(max-height:720px)]:h-9 [@media(max-height:720px)]:w-9">
                        <ArrowRight
                          size={18}
                          className="transition group-hover:translate-x-0.5 [@media(max-height:720px)]:h-4 [@media(max-height:720px)]:w-4"
                          strokeWidth={2.5}
                          aria-hidden
                        />
                      </span>
                    </Link>

                    {activeIndustry?.comingSoonSub && (
                      <p className="rounded-lg border border-black/[0.06] bg-[#f7f4ec]/60 px-3 py-2 text-[11px] leading-snug text-gray-600 line-clamp-2 [@media(max-height:720px)]:hidden">
                        {activeIndustry.comingSoonSub}
                      </p>
                    )}
                  </div>

                  <div className="mb-2 flex shrink-0 items-center justify-between gap-3 border-b border-black/[0.08] pb-2 [@media(max-height:780px)]:mb-1.5 [@media(max-height:720px)]:mb-1 [@media(max-height:720px)]:pb-1.5">
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-400">
                        Sub-topics
                      </p>
                      <p className="mt-0.5 text-xs text-black/45 [@media(max-height:720px)]:hidden">
                        Browse topics below
                      </p>
                    </div>
                    <span className="shrink-0 rounded-md border border-black/[0.06] bg-[#f7f4ec] px-2.5 py-1 text-xs font-bold tabular-nums text-black/70">
                      {activeCategories.length}
                    </span>
                  </div>

                  <div className="min-h-0 flex-1 overflow-hidden">
                  <div className="grid min-h-0 min-w-0 grid-cols-3 gap-2.5 lg:[@media(max-height:920px)]:grid-cols-4 lg:[@media(max-height:820px)]:grid-cols-5 lg:[@media(max-height:700px)]:grid-cols-6 lg:[@media(max-height:560px)]:grid-cols-7 lg:[@media(max-height:640px)]:gap-1.5 lg:[@media(max-height:560px)]:gap-1">
                    {activeCategories.map((cat) => {
                      const isCatActive = activeCategoryId === cat.id;
                      return (
                        <Link
                          key={cat.id}
                          to={cat.path}
                          onMouseEnter={() => setActiveCategoryId(cat.id)}
                          onClick={() => setIsCategoriesOpen(false)}
                          style={
                            isCatActive
                              ? {
                                  borderLeftColor: activeIndustry?.color,
                                }
                              : undefined
                          }
                          className={`group relative flex min-w-0 items-start gap-2.5 overflow-hidden rounded-xl border border-black/[0.08] border-l-[3px] bg-white/95 px-4 py-3.5 transition-all [@media(max-height:880px)]:gap-2 [@media(max-height:880px)]:px-3 [@media(max-height:880px)]:py-2.5 [@media(max-height:780px)]:gap-1.5 [@media(max-height:780px)]:px-2.5 [@media(max-height:780px)]:py-2 [@media(max-height:720px)]:gap-1.5 [@media(max-height:720px)]:px-2 [@media(max-height:720px)]:py-1.5 [@media(max-height:640px)]:rounded-lg ${
                            isCatActive
                              ? "bg-[#FFD221]/10 shadow-md ring-1 ring-[#FFD221]/25"
                              : "border-l-black/[0.14] hover:border-l-black/25 hover:bg-white hover:shadow-md"
                          }`}
                        >
                          <div
                            className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors [@media(max-height:880px)]:h-9 [@media(max-height:880px)]:w-9 [@media(max-height:720px)]:h-7 [@media(max-height:720px)]:w-7 [@media(max-height:720px)]:rounded-md ${
                              isCatActive ? "" : "bg-gray-100 group-hover:bg-[#f7f4ec]"
                            }`}
                            style={
                              isCatActive
                                ? {
                                    background: `${activeIndustry?.color}22`,
                                  }
                                : undefined
                            }
                          >
                            <ChevronRight
                              size={16}
                              strokeWidth={2.5}
                              className="shrink-0 transition-transform group-hover:translate-x-0.5"
                              style={{
                                color: isCatActive
                                  ? activeIndustry?.color
                                  : "#6b7280",
                              }}
                            />
                          </div>
                          <div className="min-w-0 flex-1 space-y-0.5">
                            <p
                              className={`text-sm font-bold leading-tight transition-colors sm:text-base [@media(max-height:880px)]:text-[13px] [@media(max-height:780px)]:text-[12px] [@media(max-height:720px)]:text-[11px] ${
                                isCatActive
                                  ? "text-black"
                                  : "text-gray-800 group-hover:text-black"
                              }`}
                            >
                              {cat.label}
                            </p>
                            {cat.description && (
                              <p className="text-xs leading-tight text-gray-500 line-clamp-1 group-hover:text-gray-600 sm:text-sm [@media(max-height:880px)]:text-[10px] [@media(max-height:560px)]:text-[9px]">
                                {cat.description}
                              </p>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                  </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* Footer strip */}
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-black/10 bg-[#f7f4ec] px-4 py-2 [@media(max-height:880px)]:py-1.5 [@media(max-height:720px)]:gap-2 [@media(max-height:720px)]:px-3 [@media(max-height:720px)]:py-1.5">
              <p className="text-xs leading-snug text-gray-500 [@media(max-height:720px)]:line-clamp-1 [@media(max-height:720px)]:text-[11px]">
                <span className="font-semibold text-gray-700">
                  {industries.length} categories
                </span>{" "}
                ·{" "}
                {industries.reduce(
                  (acc, ind) =>
                    acc + (menuByIndustry[ind.id]?.length ?? 0),
                  0,
                )}{" "}
                topics covered
              </p>
              <Link
                to="/about"
                className="shrink-0 text-xs font-semibold text-gray-500 transition-colors hover:text-black"
                onClick={() => setIsCategoriesOpen(false)}
              >
                About Infoboxx →
              </Link>
            </div>
          </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body,
        )}

      {/* ── Mobile drawer ─────────────────────────────────────────── */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            id="guest-mobile-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Main menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-x-hidden overflow-y-auto overscroll-y-contain border-t border-black/10 bg-[#f7f4ec] shadow-[0_-12px_40px_-16px_rgba(0,0,0,0.12)] [-webkit-overflow-scrolling:touch]"
            style={{
              maxHeight:
                "calc(100svh - 5rem - env(safe-area-inset-top, 0px))",
            }}
          >
            <div className="px-3 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:px-4 sm:pt-4 space-y-1">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className={`flex min-h-[48px] items-center gap-3 rounded-xl px-4 py-3 text-base font-semibold transition-colors ${
                  isActive("/")
                    ? "text-black bg-[#FFD221] shadow-sm"
                    : "text-black/75 active:bg-white hover:bg-white hover:text-black"
                }`}
              >
                <Home size={18} className="shrink-0" aria-hidden />
                Home
              </Link>

              {/* ── Explore categories toggle ── */}
              <button
                type="button"
                onClick={() => {
                  setMobileCategoriesOpen((v) => !v);
                  setMobileView("categories");
                }}
                aria-expanded={mobileCategoriesOpen}
                className="flex min-h-[3rem] justify-between items-center px-4 py-3 w-full text-left text-base font-bold text-black rounded-xl border border-black/10 bg-white/90 shadow-sm transition-colors active:bg-white"
              >
                <span className="flex items-center gap-2.5 min-w-0">
                  <Sparkles size={18} className="shrink-0" style={{ color: "#FFD221" }} aria-hidden />
                  <span className="truncate">Explore categories</span>
                </span>
                <ChevronDown
                  size={18}
                  className={`shrink-0 text-black/40 transition-transform duration-200 ${mobileCategoriesOpen ? "rotate-180" : ""}`}
                  aria-hidden
                />
              </button>

              {/* ── Mobile categories — two-step: list → sub-topics ── */}
              <AnimatePresence initial={false}>
                {mobileCategoriesOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="relative overflow-hidden">
                      <AnimatePresence mode="wait" initial={false}>

                        {/* ── Step 1: Category list ── */}
                        {mobileView === "categories" && (
                          <motion.div
                            key="cat-list"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                            className="pt-2 pb-1 flex flex-col gap-1.5"
                          >
                            {industries.map((ind) => {
                              const IndIcon = INDUSTRY_ICONS[ind.id] || Home;
                              return (
                                <button
                                  key={ind.id}
                                  type="button"
                                  onClick={() => {
                                    setMobileIndustryId(ind.id);
                                    setMobileView("subtopics");
                                    setTimeout(() => mobileSubtopicsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
                                  }}
                                  className="flex min-h-[52px] w-full items-center gap-3 rounded-xl border border-black/10 bg-white px-3 py-2.5 text-left transition-all active:scale-[0.99] active:bg-[#FFD221]/20"
                                >
                                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#f7f4ec] text-black/50">
                                    <IndIcon size={19} strokeWidth={2.1} aria-hidden />
                                  </span>
                                  <span className="min-w-0 flex-1 text-[15px] font-semibold leading-tight text-black/85">
                                    {ind.label}
                                  </span>
                                  <ChevronRight size={18} className="shrink-0 text-black/25" aria-hidden />
                                </button>
                              );
                            })}
                          </motion.div>
                        )}

                        {/* ── Step 2: Sub-topics for selected industry ── */}
                        {mobileView === "subtopics" && (
                          <motion.div
                            key="subtopics"
                            ref={mobileSubtopicsRef}
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 30 }}
                            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                            className="pt-2 pb-1 space-y-2"
                          >
                            {/* Back button */}
                            <button
                              type="button"
                              onClick={() => setMobileView("categories")}
                              className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-black/55 transition-colors active:text-black hover:text-black"
                            >
                              <ChevronRight size={15} className="rotate-180 shrink-0" aria-hidden />
                              All categories
                            </button>

                            {/* Industry hub CTA */}
                            {(() => {
                              const HubIcon = INDUSTRY_ICONS[mobileIndustryId] || Home;
                              return (
                                <Link
                                  to={mobileIndustry?.path || `/${mobileIndustryId}`}
                                  onClick={() => setIsMenuOpen(false)}
                                  style={{ borderLeftColor: mobileIndustry?.color ?? "#FFD221" }}
                                  className="group flex min-h-[56px] w-full items-center gap-3 overflow-hidden rounded-xl border border-black/[0.08] border-l-[3px] bg-gradient-to-r from-[#FFD221]/25 via-[#FFD221]/10 to-white px-3 py-3 transition-all active:brightness-95"
                                >
                                  <span
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                                    style={{ background: `${mobileIndustry?.color ?? "#FFD221"}22`, color: mobileIndustry?.color ?? "#333" }}
                                  >
                                    <HubIcon size={20} strokeWidth={2.1} aria-hidden />
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
                                      Industry hub
                                    </p>
                                    <p className="mt-0.5 text-[15px] font-black leading-tight text-gray-950 line-clamp-1">
                                      {mobileIndustry?.label}
                                    </p>
                                  </div>
                                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#FFD221]">
                                    <ArrowRight size={17} strokeWidth={2.5} aria-hidden />
                                  </span>
                                </Link>
                              );
                            })()}

                            {/* Sub-topic cards */}
                            <div className="space-y-1.5">
                              <p className="px-1 text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400">
                                Sub-topics
                              </p>
                              <AnimatePresence mode="wait" initial={false}>
                                <motion.div
                                  key={mobileIndustryId}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: 0.15 }}
                                  className="space-y-1"
                                >
                                  {mobileCategories.map((cat) => {
                                    return (
                                      <Link
                                        key={cat.id}
                                        to={cat.path}
                                        onClick={() => setIsMenuOpen(false)}
                                        style={{ borderLeftColor: mobileIndustry?.color ?? "#6b7280" }}
                                        className="flex min-h-[52px] items-center gap-3 rounded-xl border border-black/[0.08] border-l-[3px] bg-white/90 px-3 py-2.5 transition-all active:scale-[0.99] active:bg-[#FFD221]/10"
                                      >
                                        <span
                                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100"
                                          style={{ color: mobileIndustry?.color ?? "#6b7280" }}
                                        >
                                          <ChevronRight size={16} strokeWidth={2.5} aria-hidden />
                                        </span>
                                        <div className="min-w-0 flex-1 space-y-0.5">
                                          <p className="text-[14px] font-bold leading-tight text-gray-900 line-clamp-2">
                                            {cat.label}
                                          </p>
                                          {cat.description && (
                                            <p className="text-xs leading-snug text-gray-500 line-clamp-1">
                                              {cat.description}
                                            </p>
                                          )}
                                        </div>
                                        <ChevronRight size={16} className="shrink-0 text-black/20" aria-hidden />
                                      </Link>
                                    );
                                  })}
                                </motion.div>
                              </AnimatePresence>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Static links */}
              {[
                { to: "/about", label: "About", icon: Info },
                { to: "/partners", label: "Partners", icon: Users },
                { to: "/faqs", label: "FAQs", icon: CircleHelp },
                { to: "/contact", label: "Contact", icon: Mail },
              ].map(({ to, label, icon: Icon }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex min-h-[48px] items-center gap-3 rounded-xl px-4 py-3 text-base font-semibold transition-colors ${
                    isActive(to)
                      ? "text-black bg-[#FFD221] shadow-sm"
                      : "text-black/75 active:bg-white hover:bg-white hover:text-black"
                  }`}
                >
                  <Icon size={18} className="shrink-0" aria-hidden />
                  {label}
                </Link>
              ))}

              {/* Auth */}
              <div className="pt-3 mt-3 space-y-2 border-t border-black/10 sm:pt-4 sm:mt-4">
                {isAuthenticated && !isPartnersRoute ? (
                  <AccountNavMobileSection
                    onClose={() => setIsMenuOpen(false)}
                  />
                ) : (
                  <>
                    {isPartnersRoute ? (
                      <a
                        href={signInHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex min-h-[48px] items-center gap-3 rounded-xl px-4 py-3 text-base font-semibold text-black/80 transition-colors active:bg-white hover:bg-white hover:text-black"
                      >
                        <LogIn size={18} aria-hidden />
                        Sign In
                      </a>
                    ) : (
                      <Link
                        to={signInHref}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex min-h-[48px] items-center gap-3 rounded-xl px-4 py-3 text-base font-semibold text-black/80 transition-colors active:bg-white hover:bg-white hover:text-black"
                      >
                        <LogIn size={18} aria-hidden />
                        Sign In
                      </Link>
                    )}
                    <Link
                      to={registerHref}
                      onClick={() => setIsMenuOpen(false)}
                      className="flex gap-2 justify-center items-center min-h-[48px] px-4 py-3 w-full text-base font-black text-black rounded-xl border border-black/10 transition active:scale-[0.99] hover:opacity-95"
                      style={{ background: "#FFD221" }}
                    >
                      <UserPlus size={18} aria-hidden />
                      {registerLabel}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default GuestNavbar;
